/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import ModalPortal from "components-new/ModalPortal";
import InputTemp from "components-new/InputTemp";
import TextArea from "components-new/Textarea";
import { ITableDataModal } from "pages/StepPages/StepFour/One/Tables/models";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import sass from "./objects.module.sass";
import { useParams } from "react-router-dom";
import GroupCheck from "components-new/GroupCheck";
import { ChangeActiveTabContext, ITab } from "../TableNavigate";
import { snackWrapper } from "helpers";
import { InfluenceObjectParams, InfluenceObjectResponse } from "libs/apiClient/data-contracts";
import { organizationAPI } from "libs/apiClient/Organization";
import { directoryAPI } from "libs/apiClient/Directory";

interface IObjectOfImpactModal extends ITableDataModal<InfluenceObjectResponse> {
	options?: { name: string; fields: { type: string; influence: string; id: number }[] }[];
}

const initial: InfluenceObjectResponse = {
	id: 0,
	riskType: undefined,
	type: "",
	influence: "",
	consequences: "",
};

const typesRisks: { type: typeof initial.riskType; name: string }[] = [
	{
		type: "FIRST",
		name: "У1",
	},
	{
		type: "SECOND",
		name: "У2",
	},
	{
		type: "THIRD",
		name: "У3",
	},
];

const ObjectOfImpactModal: React.FC<IObjectOfImpactModal> = ({ toggle, replace, active, data = [], close }) => {
	const [currentObject, setCurrentObject] = useState<InfluenceObjectResponse>(initial);
	const params = useParams();
	const { updateTabs, changeTab } = useContext(ChangeActiveTabContext);

	const [actualType, setActualType] = useState<Array<"FIRST" | "SECOND" | "THIRD">>([]);

	const [options, setOptions] = useState<{ id: number; name: string; type: "FIRST" | "SECOND" | "THIRD" }[]>([]);

	const changeField = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setCurrentObject((prevState) => {
			return { ...prevState, [name]: value };
		});
	};

	useEffect(() => {
		directoryAPI.dropdownInfluencesList({ id: Number(params.threadId) }).then((r) => {
			setOptions(r.data.map((_, index: number) => ({ id: index + 1, ..._ })));
			const actualFromServer = Array.from(new Set(r.data.map((_: { type: string; name: string }) => _.type)));
			setActualType(Array.from(new Set(r.data.map((_) => _.type))));
			if (actualFromServer.length === 1) {
				setCurrentObject((prevState) => ({
					...prevState,
					riskType: actualFromServer[0] as "FIRST" | "SECOND" | "THIRD",
				}));
			}
		});
		//eslint-disable-next-line
	}, [toggle]);

	const title = useMemo(() => {
		return `${toggle === "edit" ? "Редактирование: " : "Создание: "}Объекты воздействия и виды воздействия на них`;
	}, [toggle]);

	const filteredOptions = useMemo(() => {
		if (currentObject.riskType) {
			return options.filter((_) => _.type === currentObject.riskType);
		}
		return options;
	}, [options, currentObject]);

	useEffect(() => {
		if (active) {
			setCurrentObject(active);
		} else {
			setCurrentObject(initial);
		}
	}, [active, toggle]);
	const save = () => {
		if (toggle === "edit") {
			const { id, ...rest } = currentObject;
			organizationAPI.objectInfluenceUpdate(id, rest as InfluenceObjectParams).then((r) => {
				const response = r.data;
				const newData = [...data!].map((_) => (_.id === response.id ? response : _));
				replace(newData);
				setCurrentObject(initial);
				snackWrapper("success", "Таблица обновлена");
				updateTabs("INFLUENCE");
				close();
			});
		} else if (toggle === "create") {
			let upload: InfluenceObjectParams;
			const { id, ...rest } = currentObject;
			upload = rest as InfluenceObjectParams;
			organizationAPI.objectInfluenceCreate(Number(params.threadId), { ...upload }).then((r) => {
				const newData = [...data!, r.data];
				replace(newData);
				setCurrentObject(initial);
				updateTabs("INFLUENCE");
				snackWrapper("success", "Таблица обновлена");
				close();
			});
		}
	};

	const disabled = useMemo(() => {
		return !currentObject.consequences || !currentObject.type || !currentObject.riskType;
	}, [currentObject]);

	return toggle !== null ? (
		<ModalPortal
			outSide={false}
			size={actualType.length ? "md" : "sl"}
			title={actualType.length ? title : "Не достаточно данных"}
			handleClose={close}>
			<div className={sass.modal}>
				{actualType.length ? (
					<>
						<GroupCheck.Wrapper label={"Вид ущерба"} required={true}>
							{typesRisks.map((_, index) => {
								return (
									<GroupCheck.Item
										disabled={!actualType.includes(_.type!)}
										key={index}
										checked={currentObject.riskType === _.type}
										onChange={() => {
											setCurrentObject((prevState) => ({ ...prevState, riskType: _.type, consequences: "" }));
										}}
										label={_.name}
										type={"radio"}
										name={"level"}
									/>
								);
							})}
						</GroupCheck.Wrapper>
						<InputTemp
							options={filteredOptions}
							filter={true}
							emptyOptions={false}
							name={"consequences"}
							className={sass.input}
							placeholder={"Введите текст"}
							onSelect={(e) => {
								setCurrentObject((prevState) => ({
									...prevState,
									consequences: e.name,
									riskType: prevState.riskType === null ? e.type : prevState.riskType,
								}));
							}}
							onChange={changeField}
							field={["name"]}
							value={currentObject.consequences || ""}
							label={"Негативные последствия"}
							required={true}
						/>

						<TextArea
							placeholder={"Введите текст"}
							rows={5}
							name={"influence"}
							onChange={changeField}
							value={currentObject.influence}
							label={"Объекты воздействия"}
						/>
						<TextArea
							placeholder={"Введите текст"}
							rows={5}
							name={"type"}
							onChange={changeField}
							value={currentObject.type || ""}
							label={"Виды воздействия"}
							required={true}
						/>
						<div className={sass.btn}>
							<Button onClick={close} variant={BUTTON_VARIANTS.OUTLINED}>
								Отменить
							</Button>
							<Button disabled={disabled} onClick={save} variant={BUTTON_VARIANTS.CONTAINED}>
								Сохранить
							</Button>
						</div>
					</>
				) : (
					<div
						onClick={() => {
							changeTab(
								{
									id: 2,
									tab: "description",
									name: "Описание системы и ее характеристика как объекта защиты",
								} as ITab,
								false,
							);
						}}
						className={sass.selectBox_list_empty}>
						Не выбраны актуальные негативные последствия на <span className={sass.link_go_to}>таблице 1</span>
					</div>
				)}
			</div>
		</ModalPortal>
	) : null;
};

export default ObjectOfImpactModal;
